import { create } from 'zustand';
import { claimflowAuthService } from '../api/authService';
import type { ClaimFlowAuthState, ClaimFlowLoginRequest } from './types';

const TOKEN_KEY = 'claimflow_auth_token';
const TENANT_KEY = 'claimflow_active_tenant_id';
const SPOOF_TENANT_KEY = 'claimflow_spoofed_tenant_id';
const SPOOF_BEARER_KEY = 'claimflow_spoofed_bearer_token';
const SPOOF_SESSION_KEY = 'claimflow_session_id';
const SPOOF_SESSION_LABEL_KEY = 'claimflow_spoofed_session_label';
const AVAILABLE_SESSIONS_KEY = 'claimflow_available_sessions';
const CD_UTIL_KEY = 'claimflow_cd_util';

export const useClaimFlowAuthStore = create<ClaimFlowAuthState>((set) => ({
  token: localStorage.getItem(TOKEN_KEY),
  user: null,
  tenants: [],
  activeTenantId: localStorage.getItem(TENANT_KEY),
  isAuthenticated: !!localStorage.getItem(TOKEN_KEY),
  isLoading: false,
  error: null,
  spoofedTenantId: localStorage.getItem(SPOOF_TENANT_KEY),
  spoofedBearerToken: localStorage.getItem(SPOOF_BEARER_KEY),
  spoofedSessionId: (() => { const v = localStorage.getItem(SPOOF_SESSION_KEY); return v ? Number(v) : null; })(),
  spoofedSessionLabel: localStorage.getItem(SPOOF_SESSION_LABEL_KEY),
  availableSessions: (() => { try { return JSON.parse(localStorage.getItem(AVAILABLE_SESSIONS_KEY) ?? '[]'); } catch { return []; } })(),
  cdUtil: localStorage.getItem(CD_UTIL_KEY),

  login: async (credentials: ClaimFlowLoginRequest) => {
    set({ isLoading: true, error: null });
    try {
      const response = await claimflowAuthService.login(credentials);
      const { token, user } = response.data;
      const tenants = user.tenants ?? [];
      localStorage.setItem(TOKEN_KEY, token);
      const persisted = localStorage.getItem(TENANT_KEY);
      const firstTenant = tenants[0];
      const activeTenantId =
        persisted ?? (firstTenant ? String(firstTenant.id) : null);
      if (activeTenantId) {
        localStorage.setItem(TENANT_KEY, activeTenantId);
      }
      set({
        token,
        user,
        tenants,
        activeTenantId,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Login failed';
      const apiMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? message;
      set({ error: apiMessage, isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    try {
      await claimflowAuthService.logout();
    } catch {
      // Ignore logout API errors - clear local state regardless
    } finally {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TENANT_KEY);
      localStorage.removeItem(SPOOF_TENANT_KEY);
      localStorage.removeItem(SPOOF_BEARER_KEY);
      localStorage.removeItem(SPOOF_SESSION_KEY);
      localStorage.removeItem(SPOOF_SESSION_LABEL_KEY);
      localStorage.removeItem(AVAILABLE_SESSIONS_KEY);
      localStorage.removeItem(CD_UTIL_KEY);
      set({
        token: null,
        user: null,
        tenants: [],
        activeTenantId: null,
        spoofedTenantId: null,
        spoofedBearerToken: null,
        spoofedSessionId: null,
        spoofedSessionLabel: null,
        availableSessions: [],
        isAuthenticated: false,
        cdUtil: null,
      });
    }
  },

  checkAuth: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    set({ isAuthenticated: !!token, token });
  },

  clearError: () => {
    set({ error: null });
  },

  setActiveTenant: (tenantId: string) => {
    localStorage.setItem(TENANT_KEY, tenantId);
    set({ activeTenantId: tenantId });
  },

  fetchUser: async () => {
    try {
      const response = await claimflowAuthService.me();
      const user = response.data;
      const tenants = user.tenants ?? [];
      const persisted = localStorage.getItem(TENANT_KEY);
      const firstTenant = tenants[0];
      const activeTenantId =
        persisted ?? (firstTenant ? String(firstTenant.id) : null);
      if (activeTenantId) {
        localStorage.setItem(TENANT_KEY, activeTenantId);
      }
      set({ user, tenants, activeTenantId });
    } catch {
      // Ignore fetch errors - user will remain null
    }
  },

  setSpoofedTenantId: (tenantId: string | null) => {
    if (tenantId) {
      localStorage.setItem(SPOOF_TENANT_KEY, tenantId);
    } else {
      localStorage.removeItem(SPOOF_TENANT_KEY);
    }
    set({ spoofedTenantId: tenantId });
  },

  setSpoofedBearerToken: (token: string | null) => {
    if (token) {
      localStorage.setItem(SPOOF_BEARER_KEY, token);
    } else {
      localStorage.removeItem(SPOOF_BEARER_KEY);
    }
    set({ spoofedBearerToken: token });
  },

  setSpoofedSessionId: (sessionId: number | null, label?: string | null) => {
    if (sessionId === null || sessionId === undefined) {
      localStorage.removeItem(SPOOF_SESSION_KEY);
      localStorage.removeItem(SPOOF_SESSION_LABEL_KEY);
    } else {
      localStorage.setItem(SPOOF_SESSION_KEY, String(sessionId));
      if (label) {
        localStorage.setItem(SPOOF_SESSION_LABEL_KEY, label);
      } else {
        localStorage.removeItem(SPOOF_SESSION_LABEL_KEY);
      }
    }
    set({ spoofedSessionId: sessionId, spoofedSessionLabel: label ?? null });
  },

  setAvailableSessions: (sessions) => {
    localStorage.setItem(AVAILABLE_SESSIONS_KEY, JSON.stringify(sessions));
    set({ availableSessions: sessions });
  },

  clearSpoofing: () => {
    localStorage.removeItem(SPOOF_TENANT_KEY);
    localStorage.removeItem(SPOOF_BEARER_KEY);
    localStorage.removeItem(SPOOF_SESSION_KEY);
    localStorage.removeItem(SPOOF_SESSION_LABEL_KEY);
    set({ spoofedTenantId: null, spoofedBearerToken: null, spoofedSessionId: null, spoofedSessionLabel: null });
  },

  setCdUtil: (cdUtil: string | null) => {
    if (cdUtil) {
      localStorage.setItem(CD_UTIL_KEY, cdUtil);
    } else {
      localStorage.removeItem(CD_UTIL_KEY);
    }
    set({ cdUtil });
  },
}));
